import { promises as fs } from 'node:fs';
import path from 'node:path';
import type { LoggingConfig } from '../types';

export type LogLevel = 'error' | 'warn' | 'info' | 'debug' | 'trace';

export class Logger {
  private config: LoggingConfig;
  private levels: Record<LogLevel, number> = {
    error: 0,
    warn: 1,
    info: 2,
    debug: 3,
    trace: 4,
  };

  constructor(config: LoggingConfig) {
    this.config = config;
  }

  error(message: string, error?: Error): void {
    this.log('error', message, { error });
  }

  warn(message: string, meta?: any): void {
    this.log('warn', message, meta);
  }

  info(message: string, meta?: any): void {
    this.log('info', message, meta);
  }

  debug(message: string, meta?: any): void {
    this.log('debug', message, meta);
  }

  trace(message: string, meta?: any): void {
    this.log('trace', message, meta);
  }

  private async log(level: LogLevel, message: string, meta?: any): Promise<void> {
    // Check if this level should be logged
    if (this.levels[level] > this.levels[this.config.level]) {
      return;
    }

    const entry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      ...meta,
    };

    // Format output
    const formatted =
      this.config.format === 'json' ? JSON.stringify(entry) : this.formatText(entry);

    // Write to console
    this.writeToConsole(level, formatted);

    // Write to file if configured
    if (this.config.file) {
      await this.writeToFile(formatted);
    }
  }

  private formatText(entry: any): string {
    const timestamp = entry.timestamp;
    const level = entry.level.toUpperCase().padEnd(5);
    const message = entry.message;

    let output = `[${timestamp}] ${level} ${message}`;

    if (entry.error) {
      output += `\n  ${entry.error.message}`;
      if (entry.error.stack && this.config.level === 'debug') {
        output += `\n${entry.error.stack}`;
      }
    }

    return output;
  }

  private writeToConsole(level: LogLevel, message: string): void {
    const methods: Record<LogLevel, (...args: any[]) => void> = {
      error: console.error,
      warn: console.warn,
      info: console.log,
      debug: console.debug,
      trace: console.trace,
    };

    methods[level](message);
  }

  private async writeToFile(message: string): Promise<void> {
    if (!this.config.file) return;

    try {
      const dir = path.dirname(this.config.file);
      await fs.mkdir(dir, { recursive: true });
      await fs.appendFile(this.config.file, `${message}\n`, 'utf-8');
    } catch (error) {
      // Silently fail to avoid recursive logging
      console.error('Failed to write to log file:', error);
    }
  }
}
